import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { compressImage } from "../../lib/imageCompress";
import { addDoc, collection, doc, onSnapshot, orderBy, query, updateDoc, writeBatch } from "firebase/firestore";
import { paths, type PricebookItem } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label } from "@clevis/shared-ui";
import { db, storage } from "../../lib/firebase";
import { useAuth } from "../../hooks/useAuth";
import { PricebookList, type PricebookBulk } from "../../components/PricebookList";

type Row = PricebookItem & { id: string };
type Comp = { itemId: string; name: string; quantity: number };
const money = (c: number) => `$${(c / 100).toFixed(2)}`;

// One flat list: search, sort, filter (category, kind, what's missing) and
// fix many items at once. No drill-downs, no broken images.
export function PricebookPage() {
  const { tenantId } = useAuth();
  const [items, setItems] = useState<Row[]>([]);
  const [note, setNote] = useState("");
  const formRef = useRef<HTMLDivElement>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", description: "", category: "", price: "", cost: "", pieceRate: "" });
  const [busy, setBusy] = useState(false);
  // Kits: bundled items sold as one line at one price.
  const [formKind, setFormKind] = useState<"item" | "kit">("item");
  const [components, setComponents] = useState<Comp[]>([]);
  const [showComponents, setShowComponents] = useState(true);
  const [compSearch, setCompSearch] = useState("");
  // The item's picture: what's saved, or a new one picked (uploaded on save).
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(query(collection(db, paths.tenantPricebook(tenantId)), orderBy("name")), (snap) =>
      setItems(snap.docs.map((d) => ({ id: d.id, ...(d.data() as PricebookItem) }))),
    );
  }, [tenantId]);

  // Bulk edits, 400 writes per batch.
  async function batchUpdate(ids: string[], patch: Record<string, unknown>) {
    if (!tenantId) return;
    for (let i = 0; i < ids.length; i += 400) {
      const batch = writeBatch(db);
      for (const id of ids.slice(i, i + 400)) batch.update(doc(db, paths.tenantPricebookItem(tenantId, id)), { ...patch, updatedAt: Date.now() });
      await batch.commit();
    }
  }
  const bulk: PricebookBulk = {
    setPieceRate: (ids, cents) => batchUpdate(ids, { pieceRateCents: cents }),
    setCategory: (ids, category) => batchUpdate(ids, { category }),
    setArchived: (ids, archived) => batchUpdate(ids, { archivedAt: archived ? Date.now() : null }),
  };

  function startEdit(item: Row) {
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description,
      category: item.category,
      price: (item.priceCents / 100).toFixed(2),
      cost: item.costCents !== null ? (item.costCents / 100).toFixed(2) : "",
      pieceRate: item.pieceRateCents ? (item.pieceRateCents / 100).toFixed(2) : "",
    });
    setFormKind(item.kind === "kit" ? "kit" : "item");
    setComponents((item.components ?? []).map((c) => ({ itemId: c.itemId, name: c.name, quantity: c.quantity })));
    setShowComponents(item.showComponents !== false);
    setPhotoUrl(item.photoUrl ?? "");
    setPhotoFile(null);
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  // What the components cost you, rolled up — the kit's default internal cost.
  const rolledCostCents = useMemo(
    () =>
      components.reduce((sum, c) => {
        const item = items.find((i) => i.id === c.itemId);
        return sum + Math.round(c.quantity * (item?.costCents ?? 0));
      }, 0),
    [components, items],
  );

  const compMatches = useMemo(() => {
    const term = compSearch.trim().toLowerCase();
    if (!term) return [];
    return items
      .filter((i) => !i.archivedAt && i.kind !== "kit" && !components.some((c) => c.itemId === i.id))
      .filter((i) => i.name.toLowerCase().includes(term) || (i.sku ?? "").toLowerCase().includes(term))
      .slice(0, 6);
  }, [items, compSearch, components]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    try {
      const isKit = formKind === "kit";
      let picture = photoUrl;
      if (photoFile) {
        const blob = await compressImage(photoFile);
        const ref = storageRef(storage, `tenants/${tenantId}/pricebookPhotos/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`);
        await uploadBytes(ref, blob, { contentType: blob.type || "image/jpeg" });
        picture = await getDownloadURL(ref);
      }
      const data = {
        name: form.name,
        description: form.description,
        category: form.category,
        priceCents: Math.round(parseFloat(form.price || "0") * 100),
        // A kit's cost defaults to its components' rolled-up cost.
        costCents: form.cost
          ? Math.round(parseFloat(form.cost) * 100)
          : isKit && rolledCostCents > 0
            ? rolledCostCents
            : null,
        // What a tech earns per unit installed on a piece-rate job.
        pieceRateCents: form.pieceRate ? Math.round(parseFloat(form.pieceRate) * 100) : null,
        taxable: true,
        kind: formKind,
        components: isKit ? components : [],
        showComponents,
        photoUrl: picture,
        updatedAt: Date.now(),
      };
      if (isKit && components.length === 0) {
        setBusy(false);
        return;
      }
      if (editingId) {
        await updateDoc(doc(db, paths.tenantPricebookItem(tenantId, editingId)), data);
      } else {
        await addDoc(collection(db, paths.tenantPricebook(tenantId)), {
          ...data,
          archivedAt: null,
          createdAt: Date.now(),
        });
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ name: "", description: "", category: "", price: "", cost: "", pieceRate: "" });
      setFormKind("item");
      setComponents([]);
      setShowComponents(true);
      setCompSearch("");
      setPhotoUrl("");
      setPhotoFile(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold">
          Pricebook <span className="text-sm font-normal text-steel">{items.filter((i) => !i.archivedAt).length}</span>
        </h1>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setEditingId(null);
              setForm({ name: "", description: "", category: "", price: "", cost: "", pieceRate: "" });
              setFormKind("item");
              setComponents([]);
              setShowComponents(true);
              setCompSearch("");
              setShowForm((v) => !v);
            }}
          >
            {showForm ? "Cancel" : "New item"}
          </Button>
        </div>
      </div>

      {note ? <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">{note}</p> : null}
      <div ref={formRef} />
      {showForm ? (
        <Card>
          <CardTitle>{editingId ? (formKind === "kit" ? "Edit kit" : "Edit item") : "New flat-rate item"}</CardTitle>
          <div className="mb-3 flex gap-1">
            {(
              [
                ["item", "Single item"],
                ["kit", "Kit / bundle"],
              ] as Array<["item" | "kit", string]>
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`rounded-full px-3 py-1 text-xs ${formKind === k ? "bg-ink text-white" : "bg-cream text-steel hover:text-ink"}`}
                onClick={() => setFormKind(k)}
              >
                {label}
              </button>
            ))}
          </div>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={formKind === "kit" ? "Fireplace setup" : ""} />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Openers" />
            </div>
            <div className="col-span-2">
              <Label>Description (shown to customers)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              {photoFile || photoUrl ? (
                <img src={photoFile ? URL.createObjectURL(photoFile) : photoUrl} alt="" className="h-16 w-16 rounded-lg border border-steel/15 object-cover" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-steel/30 text-[10px] text-steel">No picture</div>
              )}
              <div className="space-y-1">
                <label className="inline-flex cursor-pointer items-center rounded-lg border border-steel/30 bg-white px-3 py-1 text-xs text-ink hover:border-steel">
                  {photoFile || photoUrl ? "Replace picture" : "Add a picture"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setPhotoFile(f); }} />
                </label>
                {photoFile || photoUrl ? (
                  <button type="button" className="block text-xs text-steel hover:text-danger" onClick={() => { setPhotoFile(null); setPhotoUrl(""); }}>
                    Remove picture
                  </button>
                ) : null}
                <p className="text-[11px] text-steel">Shown on estimates the customer signs.</p>
              </div>
            </div>
            <div>
              <Label>Price $</Label>
              <Input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <Label>Your cost $ (internal)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                placeholder={formKind === "kit" && rolledCostCents > 0 ? (rolledCostCents / 100).toFixed(2) : ""}
              />
              {formKind === "kit" && rolledCostCents > 0 && !form.cost ? (
                <p className="mt-0.5 text-[11px] text-steel">Blank = components roll-up: {money(rolledCostCents)}</p>
              ) : null}
            </div>

            <div>
              <Label>Tech piece rate $ (per unit, internal)</Label>
              <Input type="number" step="0.01" min="0" value={form.pieceRate} placeholder="e.g. 25.00" onChange={(e) => setForm({ ...form, pieceRate: e.target.value })} />
              <p className="mt-0.5 text-[11px] text-steel">Paid on piece-rate calls for each one logged as installed.</p>
            </div>

            {formKind === "kit" ? (
              <div className="col-span-2 rounded-lg bg-cream p-3">
                <Label>What's in the kit</Label>
                {components.map((c, idx) => (
                  <div key={c.itemId} className="mb-1 flex items-center gap-2">
                    <Input
                      type="number"
                      min="0.25"
                      step="0.25"
                      className="w-16"
                      value={c.quantity}
                      onChange={(e) =>
                        setComponents((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, quantity: parseFloat(e.target.value) || 1 } : x)),
                        )
                      }
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{c.name}</span>
                    <button
                      type="button"
                      className="text-xs text-steel hover:text-danger"
                      onClick={() => setComponents((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      remove
                    </button>
                  </div>
                ))}
                <Input
                  placeholder="Add an item — search by name or SKU…"
                  value={compSearch}
                  onChange={(e) => setCompSearch(e.target.value)}
                />
                {compMatches.length > 0 ? (
                  <div className="mt-1 rounded-lg border border-steel/20 bg-white">
                    {compMatches.map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        className="flex w-full items-center justify-between px-2 py-1 text-left text-xs text-ink hover:bg-cream"
                        onClick={() => {
                          setComponents((prev) => [...prev, { itemId: i.id, name: i.name, quantity: 1 }]);
                          setCompSearch("");
                        }}
                      >
                        <span className="truncate">{i.name}</span>
                        <span className="ml-2 shrink-0 text-steel">{i.costCents !== null ? `cost ${money(i.costCents)}` : ""}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                {components.length === 0 ? (
                  <p className="mt-1 text-[11px] text-steel">A kit needs at least one item before it can be saved.</p>
                ) : null}
                <label className="mt-2 flex items-center gap-2 text-xs text-ink">
                  <input type="checkbox" checked={showComponents} onChange={(e) => setShowComponents(e.target.checked)} />
                  List included items on customer documents (names only — never their prices)
                </label>
              </div>
            ) : null}

            <div className="col-span-2">
              <Button type="submit" busy={busy}>
                Save {formKind === "kit" ? "kit" : "item"}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <PricebookList items={items} onEdit={startEdit} bulk={bulk} say={setNote} />
    </div>
  );
}
